import type { CommonData } from '@/components/blockEditor/blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from '@/components/blockEditor/blockEditor.types';

export function getDefaultQprtOverviewPageOneData(): CommonData {
  return {
    version: CURRENT_SCHEMA_VERSION,
    blocks: [
      {
        id: 'description',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'qprt-overview-heading',
              type: 'EducationalSlideHeading',
              props: {
                title: 'What is a QPRT?',
                variant: 'h3',
              },
            },
            {
              id: 'qprt-overview-body',
              type: 'EditableEducationalSlideBody',
              props: {
                text: 'A Qualified personal residence trust (QPRT) is a wealth transfer technique used to transfer assets outside of the grantor\u2019s estate. A QPRT is an irrevocable trust that allows the grantor to transfer a personal residence (primary or vacation home) to beneficiaries at a reduced gift tax cost, while retaining the right to live in it for a set number of years. At the end of the term, the home passes to the beneficiaries, typically at a significantly discounted value for gift tax purposes.\n\nQPRTs are most beneficial when the grantor expects the property to appreciate in value and wants to remove that appreciation from their taxable estate.',
              },
            },
          ],
        },
      },
      {
        id: 'benefits',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'qprt-overview-page-one-columns',
              type: 'TwoColumnLayout',
              props: {
                leftWidth: '50%',
                rightWidth: '50%',
                columnGap: 2,
                showDivider: true,
              },
              children: {
                leftColumn: [
                  {
                    id: 'qprt-overview-benefits-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Potential benefits',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'qprt-benefit-estate-tax',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Estate tax reduction',
                      description:
                        'QPRTs freeze the value of the home for estate tax purposes and move future home appreciation out of the estate, which can significantly reduce future estate tax liability.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'qprt-benefit-gift-tax',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Gift tax savings',
                      description:
                        'The taxable value of the gift is discounted by the IRS because the grantor retains the right to live in the home during the trust term, meaning less lifetime exemption is used than if the home is gifted outright.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'qprt-benefit-continued-use',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Continued use of the home',
                      description:
                        'The grantor retains control of the home and the right to live in the home during the trust term.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                ],
                rightColumn: [
                  {
                    id: 'qprt-overview-drawbacks-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Potential drawbacks',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'qprt-drawback-irrevocability',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Irrevocability',
                      description:
                        'Once established, a QPRT cannot be modified or revoked. This lack of flexibility can be limiting if circumstances change.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'qprt-drawback-grantor-lifetime',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Grantor lifetime considerations',
                      description:
                        'If the grantor passes away during the trust term, the full value of the home is returned to the grantor\u2019s estate and may be subject to estate tax.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'qprt-drawback-no-step-up',
                    type: 'PresentationListItem',
                    props: {
                      title: 'No step-up in cost basis',
                      description:
                        'Beneficiaries inherit the original cost basis of the home rather than a stepped-up basis, potentially creating a large capital gains tax if they later sell the property.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'qprt-drawback-loss-of-ownership',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Loss of ownership after term end',
                      description:
                        'Once the term ends, the grantor no longer owns the home and must pay fair market rent to the beneficiaries in order to continue living in the home.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'qprt-drawback-complexity',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Complexity & costs',
                      description:
                        'Creating QPRT involves legal and administrative expenses, and the structure may require ongoing professional management.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    ],
  };
}
